import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ConfirmationModal from '@enerex/unified-ui-react/ConfirmationModal';
import FormInput from '@enerex/unified-ui-react/form/FormInput';
import RadioGroup from '@enerex/unified-ui-react/form/RadioGroup';
import Select from '@enerex/unified-ui-react/form/Select';
import { CommodityIcon } from 'pages/utilities/CommodityIcons';

import { getCommodities, getSuppliers, getUtilities } from './rateClassApi';

const getCommodityType = (option) => {
  const id = Number(option?.commodityId);
  if (id === 1) return 'electric';
  if (id === 2) return 'gas';

  const commodity = String(option?.commodity || option?.label || '').trim().toLowerCase();
  if (commodity === 'electric' || commodity === 'electricity') return 'electric';
  if (commodity === 'gas' || commodity === 'natural gas') return 'gas';

  return null;
};

const AddRateClassAssociationModal = ({ show, type, utilityRows = [], onHide, onSave }) => {
  const [commodity, setCommodity] = useState(null);
  const [utility, setUtility] = useState(null);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState('');
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingCommodities, setLoadingCommodities] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const { control, reset, watch } = useForm({ defaultValues: { rateClassName: '' } });
  const rateClassName = watch('rateClassName') || '';

  const isUtility = type === 'utility';
  const isSupplier = type === 'supplier';
  const companyLabel = isUtility ? 'Utility' : 'Supplier';
  const utilityOptions = utilityRows
    .filter((item) => !commodity?.commodityId || Number(item.commodityId) === Number(commodity.commodityId))
    .map((item) => ({
      value: item.utilityRateClassId || item.utilityId,
      label: item.utilityName || '',
      utilityId: item.utilityId,
      utilityName: item.utilityName || '',
      utilityRateClassId: item.utilityRateClassId,
      commodityId: item.commodityId,
      commodity: item.commodity,
    }))
    .filter((item) => item.value && item.label);

  const handleCommodityChange = (option) => {
    setCommodity(option);
    setUtility(null);
    setCompany(null);
    setCompanyOptions([]);
  };

  const handleUtilityChange = (option) => {
    setUtility(option);
    setCompany(null);
  };

  useEffect(() => {
    if (!show) return;
    setCommodity(null);
    setUtility(null);
    setCompany(null);
    reset({ rateClassName: '' });
    setError('');
    setCompanyOptions([]);
  }, [show, type, reset]);

  useEffect(() => {
    if (!show) return;

    const loadCommodities = async () => {
      setLoadingCommodities(true);
      try {
        setCommodityOptions(await getCommodities());
      } catch (loadError) {
        setError(loadError.message || 'Failed to load commodities.');
      } finally {
        setLoadingCommodities(false);
      }
    };

    loadCommodities();
  }, [show]);

  useEffect(() => {
    if (!show || !isUtility || !commodity?.commodityId) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        setCompanyOptions(await getUtilities(commodity.commodityId));
      } catch (loadError) {
        setError(loadError.message || `Failed to load ${companyLabel.toLowerCase()}s.`);
      } finally {
        setLoadingCompanies(false);
      }
    };

    setCompany(null);
    loadCompanies();
  }, [show, commodity, isUtility, companyLabel]);

  useEffect(() => {
    if (!show || !isSupplier || !utility?.value) return;

    const loadSuppliers = async () => {
      setLoadingCompanies(true);
      try {
        setCompanyOptions(await getSuppliers());
      } catch (loadError) {
        setError(loadError.message || 'Failed to load suppliers.');
      } finally {
        setLoadingCompanies(false);
      }
    };

    setCompany(null);
    loadSuppliers();
  }, [show, isSupplier, utility]);

  const handleSave = () => {
    setError('');
    if (isSupplier && !utility?.value) {
      setError('Please select a utility.');
      return;
    }
    if (!commodity?.value) {
      setError('Please select a commodity.');
      return;
    }
    if (!company?.value) {
      setError(`Please select a ${companyLabel.toLowerCase()}.`);
      return;
    }
    if (!rateClassName.trim()) {
      setError('Please enter rate class.');
      return;
    }

    onSave({
      commodityId: commodity.commodityId,
      commodity: commodity.commodity,
      companyId: isUtility ? company.utilityId : company.supplierId,
      companyName: isUtility ? company.utilityName : company.supplierName,
      utilityId: utility?.utilityId,
      utilityName: utility?.utilityName,
      utilityRateClassId: utility?.utilityRateClassId,
      rateClassName: rateClassName.trim(),
    });
  };

  return (
    <ConfirmationModal
      centered
      dialogClassName="modal-md"
      show={show}
      onHide={onHide}
      title={isUtility ? 'Add Utility Rate Class' : 'Add Supplier Rate Class'}
      confirmButtonLabel="Save"
      confirmButtonVariant="primary"
      onConfirm={handleSave}
    >
      {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}
      <div className="mb-3">
        <div className="d-flex flex-wrap align-items-center gap-4">
          {loadingCommodities ? <span className="text-muted">Loading commodities...</span> : null}
          {!loadingCommodities ? (
            <RadioGroup
              inline
              name="rate-class-association-commodity"
              value={commodity?.value || ''}
              onChange={(value) => handleCommodityChange(commodityOptions.find((option) => String(option.value) === String(value)))}
              options={commodityOptions.map((option) => {
                const type = getCommodityType(option);

                return {
                  id: `rate-class-association-commodity-${option.value}`,
                  value: option.value,
                  label: (
                    <span className="d-inline-flex align-items-center gap-1">
                      {type ? <CommodityIcon type={type} /> : null}
                    </span>
                  ),
                };
              })}
            />
          ) : null}
        </div>
      </div>
      {isSupplier ? (
        <div className="mb-3">
          <label className="form-label">Utility</label>
          <Select value={utility} options={utilityOptions} onChange={handleUtilityChange} placeholder="Select utility" isDisabled={!commodity?.commodityId} getOptionValue={(option) => String(option.value)} getOptionLabel={(option) => option.label} />
        </div>
      ) : null}
      <div className="mb-3">
        <label className="form-label">{companyLabel}</label>
        <Select value={company} options={companyOptions} onChange={setCompany} placeholder={`Select ${companyLabel.toLowerCase()}`} isLoading={loadingCompanies} isDisabled={isSupplier && !utility?.value} getOptionValue={(option) => String(option.value)} getOptionLabel={(option) => option.label} />
      </div>
      <FormInput
        type="text"
        name="rateClassName"
        control={control}
        label={isUtility ? 'Utility Rate Class' : 'Supplier Rate Class'}
        containerClass="mb-3"
      />
    </ConfirmationModal>
  );
};

export default AddRateClassAssociationModal;
